import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Bell, AlertTriangle, Clock, X } from "lucide-react";

export default function CustomNotifications({ 
  notifications = [], 
  onDismiss, 
  onDismissAll 
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <Button 
        variant="ghost" 
        size="icon" 
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notifications-btn"
      >
        <Bell className="w-5 h-5" />
        {notifications.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {notifications.length}
          </span>
        )}
      </Button>
      
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-80 bg-popover border border-border rounded-md shadow-lg animate-in fade-in-0 zoom-in-95">
          {/* Header */}
          <div className="p-3 border-b border-border">
            <h4 className="font-semibold flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifiche Scadenze
            </h4>
          </div>
          
          {/* Content */}
          <div className="max-h-[300px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                Nessuna scadenza imminente
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id}
                    className={`p-3 rounded-lg border ${
                      notif.urgency === 'overdue' 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : notif.urgency === 'critical'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {notif.isOverdue ? (
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          )}
                          <span className={`text-xs font-medium ${
                            notif.isOverdue ? 'text-red-500' : 'text-amber-500'
                          }`}>
                            {notif.isOverdue 
                              ? `Scaduto ${Math.abs(notif.daysLeft)} giorni fa`
                              : notif.daysLeft === 0 
                              ? 'Scade oggi!'
                              : notif.daysLeft === 1
                              ? 'Scade domani'
                              : `Scade tra ${notif.daysLeft} giorni`
                            }
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{notif.info}</p>
                        <p className="text-xs text-muted-foreground">{notif.system} • {notif.country}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDismiss(notif.id);
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-border">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => {
                  onDismissAll();
                  setOpen(false);
                }}
              >
                Nascondi tutte
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
